import { useRef } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import type { ApprovedTemplateOption } from '@/components/CampaignConfigForm'
import type { CampaignConfig, CampaignFollowup, OpeningTemplateVariant } from '@sdr-agend/admin-schemas'

/**
 * Story 3.20 — generic followup-by-inactivity config section.
 *
 * Replaces the old "Cadência (C1→C2→C3)" section for campaigns that have
 * adopted the followup engine (`lead_new` in this story; `confirmation`/
 * `reactivation` migrate onto the same section in Stories 3.21/3.22).
 * Followups fire by SILENCE in the conversation (minutes since the last
 * activity, IA or customer — whichever is more recent), not by a fixed
 * time/offset relative to a meeting.
 */
export interface FollowupConfigSectionProps {
  value: CampaignConfig
  onChange: (next: CampaignConfig) => void
  approvedTemplates: ReadonlyArray<ApprovedTemplateOption>
  disabled?: boolean
}

const MAX_STEPS = 5
const MAX_TOTAL_TOUCHES = MAX_STEPS + 1

// 2026-07-21 — A/B/C opening-template variants. Letters give a friendly
// default `key` suggestion; the cap matches `OpeningTemplateVariant`'s
// `maxItems: 10` in the admin-schemas contract.
const VARIANT_KEY_LETTERS = 'ABCDEFGHIJ'.split('')
const MAX_VARIANTS = VARIANT_KEY_LETTERS.length

const DEFAULT_FOLLOWUP: CampaignFollowup = {
  initial: { trigger: 'immediate', templateName: '' },
  steps: [],
}

function newStepId(): string {
  const cryptoObj = globalThis.crypto
  return typeof cryptoObj?.randomUUID === 'function' ? cryptoObj.randomUUID() : `step-${Date.now()}`
}

function nextVariantKey(existing: ReadonlyArray<OpeningTemplateVariant>): string {
  const used = new Set(existing.map((v) => v.key))
  const letter = VARIANT_KEY_LETTERS.find((l) => !used.has(l))
  return letter ?? `V${existing.length + 1}`
}

export function FollowupConfigSection(props: FollowupConfigSectionProps): React.ReactElement {
  const { value, onChange, approvedTemplates, disabled = false } = props
  const followup = value.followup ?? DEFAULT_FOLLOWUP

  function patch(next: CampaignFollowup): void {
    onChange({ ...value, followup: next })
  }

  function patchInitial(partial: Partial<CampaignFollowup['initial']>): void {
    patch({ ...followup, initial: { ...followup.initial, ...partial } })
  }

  function patchStep(id: string, partial: Partial<CampaignFollowup['steps'][number]>): void {
    patch({
      ...followup,
      steps: followup.steps.map((s) => (s.id === id ? { ...s, ...partial } : s)),
    })
  }

  function addStep(): void {
    if (followup.steps.length >= MAX_STEPS) return
    const previousAfter = followup.steps[followup.steps.length - 1]?.afterMinutes ?? 0
    patch({
      ...followup,
      steps: [
        ...followup.steps,
        { id: newStepId(), afterMinutes: previousAfter + 60, templateName: '' },
      ],
    })
  }

  function removeStep(id: string): void {
    patch({ ...followup, steps: followup.steps.filter((s) => s.id !== id) })
  }

  const variants = followup.initial.variants ?? []

  // 2026-07-21 (QA finding — Story 8.2): stable per-row identity for React's
  // list reconciliation. `OpeningTemplateVariant` has no persisted id (only
  // `key`/`templateName`), and `key` itself is user-editable text — neither
  // is safe as a React key (typing into the key field would remount the row
  // mid-keystroke; two variants can transiently share a key while editing).
  // `addVariant`/`removeVariant` mutate this ref in lockstep with `variants`
  // so identity survives editing and removal, mirroring `steps`' UUID-per-row
  // approach without adding a UI-only field to the persisted schema. If the
  // parent ever replaces `variants` wholesale (e.g. switching campaigns) the
  // length changes without going through these two functions — regenerate.
  const variantIdsRef = useRef<string[]>(variants.map(() => newStepId()))
  if (variantIdsRef.current.length !== variants.length) {
    variantIdsRef.current = variants.map(() => newStepId())
  }
  const variantIds = variantIdsRef.current

  function addVariant(): void {
    if (variants.length >= MAX_VARIANTS) return
    variantIdsRef.current = [...variantIdsRef.current, newStepId()]
    patchInitial({ variants: [...variants, { key: nextVariantKey(variants), templateName: '' }] })
  }

  function removeVariant(index: number): void {
    variantIdsRef.current = variantIdsRef.current.filter((_, i) => i !== index)
    patchInitial({ variants: variants.filter((_, i) => i !== index) })
  }

  function patchVariant(index: number, partial: Partial<OpeningTemplateVariant>): void {
    patchInitial({ variants: variants.map((v, i) => (i === index ? { ...v, ...partial } : v)) })
  }

  const totalTouches = 1 + followup.steps.length

  return (
    <section className="grid gap-6 rounded-lg border bg-muted/20 p-5">
      <header>
        <h2 className="text-lg font-semibold">Followup</h2>
        <p className="text-sm text-muted-foreground">
          Disparo inicial + até {MAX_STEPS} cutucadas por silêncio na conversa (não por horário
          fixo). Máximo de {MAX_TOTAL_TOUCHES} toques no total.
        </p>
      </header>

      {/* Disparo inicial (equivalente ao antigo C1). */}
      <div className="grid gap-4 rounded-md border bg-card/50 p-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="fu-initial-trigger">Disparo inicial</Label>
          <Select
            id="fu-initial-trigger"
            value={followup.initial.trigger}
            onChange={(e) =>
              patchInitial({
                trigger: e.target.value as CampaignFollowup['initial']['trigger'],
                delayMinutes:
                  e.target.value === 'immediate' ? undefined : followup.initial.delayMinutes,
              })
            }
            disabled={disabled}
          >
            <option value="immediate">Imediato</option>
            <option value="delay_minutes">Esperar N minutos</option>
          </Select>
        </div>
        {followup.initial.trigger === 'delay_minutes' ? (
          <div className="grid gap-2">
            <Label htmlFor="fu-initial-delay">Minutos de espera</Label>
            <Input
              id="fu-initial-delay"
              type="number"
              min={1}
              max={10080}
              value={followup.initial.delayMinutes ?? ''}
              onChange={(e) => {
                // QA finding M1: `Number(x) || undefined` snapped the field back
                // to unset every time the input transiently held "0" (e.g. while
                // clearing it to type a new value), which then corrupted the
                // next keystroke. Only empty-string clears the field; anything
                // else is parsed as typed, with min/max enforced by validation.
                const raw = e.target.value
                if (raw === '') {
                  patchInitial({ delayMinutes: undefined })
                  return
                }
                const parsed = Number(raw)
                if (!Number.isNaN(parsed)) patchInitial({ delayMinutes: parsed })
              }}
              disabled={disabled}
            />
          </div>
        ) : null}
        <div className="grid gap-2 sm:col-span-2">
          <Label htmlFor="fu-initial-template">Template do contato inicial</Label>
          <Select
            id="fu-initial-template"
            value={followup.initial.templateName}
            onChange={(e) => patchInitial({ templateName: e.target.value })}
            disabled={disabled || approvedTemplates.length === 0}
          >
            <option value="">— selecione —</option>
            {approvedTemplates.map((t) => (
              <option key={t.name} value={t.name}>
                {t.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* 2026-07-21 — teste A/B/C do template de abertura. */}
      <div className="grid gap-3 rounded-md border bg-card/50 p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label>Teste A/B/C do template de abertura</Label>
            <p className="text-sm text-muted-foreground">
              Opcional. Cada lead cai numa variante de forma determinística (o mesmo lead sempre
              recebe a mesma variante). Sem variantes configuradas, todo lead recebe o template do
              disparo inicial acima.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addVariant}
            disabled={disabled || variants.length >= MAX_VARIANTS}
          >
            Adicionar variante
          </Button>
        </div>

        {variants.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma variante configurada.</p>
        ) : (
          <ul className="grid gap-3">
            {variants.map((variant, index) => (
              <li
                key={variantIds[index]}
                className="grid gap-3 rounded-md border bg-background p-3 sm:grid-cols-[1fr_2fr_auto]"
              >
                <div className="grid gap-2">
                  <Label htmlFor={`fu-variant-${index}-key`}>Variante {index + 1} — chave</Label>
                  <Input
                    id={`fu-variant-${index}-key`}
                    value={variant.key}
                    maxLength={32}
                    onChange={(e) => patchVariant(index, { key: e.target.value })}
                    disabled={disabled}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor={`fu-variant-${index}-template`}>Template</Label>
                  <Select
                    id={`fu-variant-${index}-template`}
                    value={variant.templateName}
                    onChange={(e) => patchVariant(index, { templateName: e.target.value })}
                    disabled={disabled || approvedTemplates.length === 0}
                  >
                    <option value="">— selecione —</option>
                    {approvedTemplates.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeVariant(index)}
                    disabled={disabled}
                  >
                    Remover
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Lista dinâmica de followups. */}
      <div className="grid gap-3">
        <div className="flex items-center justify-between">
          <Label>Followups (por silêncio na conversa)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addStep}
            disabled={disabled || followup.steps.length >= MAX_STEPS}
          >
            Adicionar followup
          </Button>
        </div>

        {followup.steps.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum followup configurado — só o disparo inicial. Se o cliente não responder, a IA não
            manda mais nada.
          </p>
        ) : (
          <ul className="grid gap-3">
            {followup.steps.map((step, index) => (
              <li
                key={step.id}
                className="grid gap-3 rounded-md border bg-card/50 p-3 sm:grid-cols-[1fr_2fr_auto]"
              >
                <div className="grid gap-2">
                  <Label htmlFor={`fu-step-${step.id}-minutes`}>
                    Etapa {index + 1} — minutos de silêncio
                  </Label>
                  <Input
                    id={`fu-step-${step.id}-minutes`}
                    type="number"
                    min={1}
                    max={10080}
                    value={step.afterMinutes}
                    onChange={(e) => {
                      // QA finding M1: `Number(x) || 1` snapped this field to 1
                      // whenever it transiently held "0" while being edited
                      // (e.g. clearing "10" to type "30"), corrupting the next
                      // keystroke. Parse whatever was typed; min/max are
                      // enforced by validation, not by mangling input here.
                      //
                      // R2-6 follow-up considered: skipping the update entirely
                      // on an empty string (to avoid ever committing 0) was
                      // tried and reverted — React's controlled-input value
                      // tracker snaps the DOM back to the FULL old value on the
                      // next keystroke when onChange doesn't call setState,
                      // which reproduces the same corruption this fix exists to
                      // prevent (confirmed via the regression test below).
                      // Committing 0 and letting the number input's own
                      // leading-zero normalization take over is the version
                      // that actually works.
                      const parsed = Number(e.target.value)
                      if (!Number.isNaN(parsed)) patchStep(step.id, { afterMinutes: parsed })
                    }}
                    disabled={disabled}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor={`fu-step-${step.id}-template`}>Template</Label>
                  <Select
                    id={`fu-step-${step.id}-template`}
                    value={step.templateName}
                    onChange={(e) => patchStep(step.id, { templateName: e.target.value })}
                    disabled={disabled || approvedTemplates.length === 0}
                  >
                    <option value="">— selecione —</option>
                    {approvedTemplates.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStep(step.id)}
                    disabled={disabled}
                  >
                    Remover
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <p className="text-xs text-muted-foreground">
          Cada etapa dispara N minutos depois da última atividade (IA ou cliente) — não uma soma das
          etapas anteriores. Se o cliente responder a qualquer momento, a contagem volta pra
          primeira etapa. Depois da última etapa sem resposta, a conversa é marcada como fria.{' '}
          {totalTouches}/{MAX_TOTAL_TOUCHES} toques configurados.
        </p>
      </div>
    </section>
  )
}

export default FollowupConfigSection
